import { emailBuilderApi } from '@/api';
import { queryKeys } from '@/api/query-keys';
import {
  EmailBuilderSendDetail,
  EmailBuilderSendSummary,
  RecipientConsentResponse,
  RenderEmailRequest,
  RenderEmailResponse,
  SendEmailRequest,
  SendEmailResponse,
} from '@/api/email-builder/email-builder.types.ts';
import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

/**
 * Renders the builder draft to HTML for the live preview pane.
 *
 * The `payload` should be debounced by the caller — the query key is derived
 * from it, so every change triggers a refetch. `keepPreviousData` keeps the
 * last good preview on screen while the next render is in flight.
 */
export const useRenderEmail = (payload: RenderEmailRequest, enabled = true) =>
  useQuery<RenderEmailResponse, AxiosError>({
    queryKey: queryKeys.emailBuilder.render(payload),
    queryFn: () => emailBuilderApi.render(payload),
    placeholderData: keepPreviousData,
    enabled,
  });

/**
 * Send the visual-builder draft to the chosen Verteiler / ad-hoc recipients.
 * Each recipient gets their own personalised render server-side.
 */
export const useSendEmailDraft = (
  options?: UseMutationOptions<SendEmailResponse, AxiosError, SendEmailRequest>,
) => {
  const queryClient = useQueryClient();
  return useMutation<SendEmailResponse, AxiosError, SendEmailRequest>({
    mutationFn: (body) => emailBuilderApi.send(body),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.emailBuilder.sends });
      options?.onSuccess?.(...args);
    },
  });
};

/** Test-send the current draft to the caller's own email. */
export const useTestSendEmailDraft = (
  options?: UseMutationOptions<SendEmailResponse, AxiosError, RenderEmailRequest>,
) => {
  const queryClient = useQueryClient();
  return useMutation<SendEmailResponse, AxiosError, RenderEmailRequest>({
    mutationFn: (body) => emailBuilderApi.testSend(body),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.emailBuilder.sends });
      options?.onSuccess?.(...args);
    },
  });
};

/**
 * Contact-employee ids that consented to mail reception (an enabled stocklist
 * subscription). Returned as a `Set` so the recipients pane can flag rows and
 * drive the "only recipients who allowed mail reception" filter (SUP-40).
 */
export const useRecipientConsent = (enabled = true) =>
  useQuery<RecipientConsentResponse, AxiosError, Set<number>>({
    queryKey: queryKeys.emailBuilder.recipientConsent,
    queryFn: () => emailBuilderApi.recipientConsent(),
    select: (data) => new Set(data.contactEmployeeIds),
    enabled,
  });

export const useEmailBuilderSends = (enabled = true) =>
  useQuery<EmailBuilderSendSummary[], AxiosError>({
    queryKey: queryKeys.emailBuilder.sends,
    queryFn: () => emailBuilderApi.listSends(),
    enabled,
  });

export const useEmailBuilderSendDetail = (sendId: number | null) =>
  useQuery<EmailBuilderSendDetail, AxiosError>({
    queryKey: queryKeys.emailBuilder.sendDetail(sendId ?? 0),
    queryFn: () => emailBuilderApi.getSend(sendId as number),
    enabled: sendId != null,
  });
